import {otsBaseApi} from "../../ots-api";
import {Message} from "../../../model/message";
import {removeUndefined} from "../../../util/util";
import {driverActivityCacheUpdate, mergeMessageIntoDriverActivity} from "../driver-activity/driver-activity-store";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Messages", "ObjectMessages", "PersonNotices"]});

export type GetAllMessagesByObjectArgs = {
  objectId: string;
  /** Uses the current typed endpoint when known; omitted for legacy callers that only have an id. */
  objectType?: string;
  tenantCode?: string;
};

export type CreateMessageArgs = Partial<Message> & {
  /** Routes creation to the same tenant used to load the owning entity. */
  tenantCode?: string;
};

const messageApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMessagesByObject: builder.query<Message[], GetAllMessagesByObjectArgs>({
      query: ({objectId, objectType, tenantCode}) => ({
        url: objectType ? `/message/${objectType}/${objectId}` : `/message/all/object/${objectId}`,
        method: "GET",
        params: removeUndefined(objectType ? {tenantCode} : {serviceLookup: tenantCode})
      }),
      providesTags: (_result, _error, arg, _meta) => {
        return [{type: 'ObjectMessages', id: arg.objectId}];
      },
    }),
    getPersonNotices: builder.query<[Message], string>({
      query: (id) => ({
        url: `/person/${id}/notices`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [
        {type: "PersonNotices", id}
      ]
    }),
    createMessage: builder.mutation<void, CreateMessageArgs>({
      query: ({tenantCode, ...message}) => ({
        url: '/message',
        method: 'POST',
        params: removeUndefined({tenantCode}),
        // A message is on reports unless the caller says otherwise, which is also how the
        // server's exporters read one with no answer.
        body: {...message, report: message.report ?? true},
      }),
      invalidatesTags: (result, error, arg, meta) => {
        return [
          {type: 'ObjectMessages', id: arg.objectId},
          {type: "PersonNotices", id: arg.objectId}
        ]
      },
      // A comment on a transport shows in cached driver activity at once, without fetching the day again.
      async onQueryStarted({tenantCode: _tenantCode, ...message}, {dispatch, queryFulfilled, getState}) {
        if (message.objectType !== "TRANSPORT" || !message.objectId) return;
        try {
          await queryFulfilled;
          const now = Date.now();
          const saved: Message = {
            id: `local-${now}`,
            objectId: message.objectId,
            objectType: message.objectType,
            messageType: message.messageType ?? "Comment",
            message: message.message ?? "",
            createDttm: now,
            lastModifiedDttm: now,
            report: message.report ?? true,
            personId: message.personId,
            personName: message.personName,
          };
          driverActivityCacheUpdate(dispatch, getState(), (draft) => mergeMessageIntoDriverActivity(draft, saved));
        } catch {
          // The mutation reports its own failure.
        }
      },
    }),
    updateMessage: builder.mutation<Message, Partial<Message>>({
      query: (message) => ({
        url: '/message',
        method: 'PUT',
        body: message,
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'Messages', id: arg.id},
        {type: 'ObjectMessages', id: arg.objectId},
        {type: "PersonNotices", id: arg.objectId}
      ],
    }),
    deleteMessage: builder.mutation<void, Partial<Message>>({
      query: (message) => ({
        url: '/message',
        method: 'DELETE',
        body: message,
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'Messages', id: arg.id},
        {type: 'ObjectMessages', id: arg.objectId},
        {type: "PersonNotices", id: arg.objectId}
      ],
    }),
  })
});

export const {
  useGetAllMessagesByObjectQuery,
  useGetPersonNoticesQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation,
  useDeleteMessageMutation,
} = messageApi;
